import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer"
import { graphql } from "gatsby"
import {
  ContentfulRichTextGatsbyReference,
  RenderRichTextData,
} from "gatsby-source-contentful/rich-text"
import React from "react"
import readingTime from "reading-time/lib/reading-time"
import { css } from "styled-components"
import Footer from "../../../components/shared/footer"
import Layout from "../../../components/shared/layout"
import SEO from "../../../components/shared/seo"
import FixedHeaderSpacer from "../../atoms/fixed-header-spacer"
import VariantContainer from "../../atoms/variant-container"
import { IContentfulComposePage } from "../../types/blog.types"
import ArticleBody from "./article-body"
import ArticleHeader, { IArticleFrontmatter } from "./article-header"
import RelatedArticles from "./related-articles"

/**
 * Template for generated Contentful Articles
 * @param data - Data from GraphQL query
 */
const ArticlePage = ({ data: { contentfulComposePage }, location }) => {
  // Get the read time from the body text
  const body: RenderRichTextData<ContentfulRichTextGatsbyReference> =
    contentfulComposePage.content.content
  const bodyAsPlainText: string = body
    ? documentToPlainTextString(JSON.parse(body.raw))
    : ""
  const readTime: string = readingTime(bodyAsPlainText).text

  // Map frontmatter data
  const frontmatter: IArticleFrontmatter = {
    title: contentfulComposePage.title,
    content: contentfulComposePage.content,
    readTime: readTime,
    href: location.href,
  }

  const relatedArticles: IContentfulComposePage[] =
    contentfulComposePage.content.relatedPosts

  return (
    <Layout showBackground={true} contactLightbox={true} location={location}>
      <SEO
        title={
          contentfulComposePage.seo.title || contentfulComposePage.title || ""
        }
        description={contentfulComposePage.seo.description || ""}
        image={`https:${contentfulComposePage.content.image?.file?.url}?w=1200&h=630`}
      />
      <FixedHeaderSpacer />
      <ArticleHeader frontmatter={frontmatter} />
      <ArticleBody body={body} />
      <VariantContainer
        variants={[
          css`
            /* Wrap footer so "to top" button falls behind  */
            position: relative;
            z-index: 2;
          `,
        ]}
      >
        <RelatedArticles articles={relatedArticles} />
        <Footer />
      </VariantContainer>
    </Layout>
  )
}

export default ArticlePage

/**
 * GraphQL query for retrieving article data
 */
export const query = graphql`
  query ($slug: String!) {
    contentfulComposePage(slug: { eq: $slug }) {
      title
      seo {
        title
        description
      }
      content {
        ... on ContentfulBlogPost {
          author
          publishDate(formatString: "MMM D, YYYY")
          updatedAt(formatString: "MMM D, YYYY")
          content {
            raw
            references {
              ... on ContentfulAsset {
                contentful_id
                __typename
                description
                file {
                  url
                  contentType
                }
              }
              ...ArticleFeatureFragment
              ...CodeBlockFragment
            }
          }
          image {
            file {
              url
            }
          }
          pdf {
            file {
              url
            }
          }
          relatedPosts {
            ...ContentfulComposePageFragment
          }
          tags {
            category {
              slug
              name
            }
            topic {
              slug
              name
            }
          }
        }
      }
    }
  }
`
